import { safeLoginRedirect } from '@/utils/safeLoginRedirect';
import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/components/AuthContext";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import apiClient from "@/lib/apiClient";
import { Helmet } from 'react-helmet-async';
import Header from "../../header";
import ReCAPTCHA from "react-google-recaptcha";
import { ThemedButton, ThemedCard, ThemedInput } from "@/components/themed";

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "";

interface Site {
  id: number;
  name: string;
  subdomain: string;
  slug: string;
  description: string;
  icon_url: string | null;
  thumbnail_url: string | null;
  privacy_url: string | null;
  terms_url: string | null;
  trade_law_url: string | null;
  created_at: string;
  identity_verified: boolean;
  antique_dealer_verified: boolean;
  delivery_count: number;
}

interface LoginPageProps {
  siteId: number;
  siteData?: Site;
}

const LoginPage: React.FC<LoginPageProps> = ({ siteId, siteData }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { login, currentUser } = useAuth();

  // ログイン済みユーザーのリダイレクト
  useEffect(() => {
    if (currentUser) {
      const params = new URLSearchParams(window.location.search);
      window.location.href = safeLoginRedirect(params.get('redirect'));
    }
  }, [currentUser]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    // reCAPTCHA検証（サイトキーが設定されている場合のみ）
    if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
      setError("reCAPTCHAにチェックを入れてください");
      return;
    }

    try {
      const response = await apiClient.post("/customer/sessions", {
        email,
        password,
        site_id: siteId,
        recaptcha_token: recaptchaToken || undefined
      });
      await login({ token: response.data.token });

      toast.success("ログインしました");

      // リダイレクト
      const params = new URLSearchParams(window.location.search);
      const redirect = safeLoginRedirect(params.get('redirect'));

      window.location.href = redirect;
    } catch (err: any) {
      const responseData = err.response?.data;

      // メール未認証エラーの場合は認証待ちページへリダイレクト
      if (responseData?.email_unverified) {
        localStorage.setItem("pendingVerificationEmail", responseData.email || email);
        window.location.href = "/email-verification-pending";
        return;
      }

      setError(responseData?.error || err.message || "ログインに失敗しました");

      // reCAPTCHAをリセット
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  return (
    <>
      <Helmet>
        <title>ログイン | {siteData?.name || 'ショップ'}</title>
        <meta property="og:title" content={`ログイン | ${siteData?.name || 'ショップ'}`} />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <Header siteData={siteData} />

        <section className="flex justify-center py-12 px-4 sm:px-6 lg:px-8 pt-24">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <h2 className="text-3xl [font-weight:var(--theme-font-weight)] tracking-tight text-theme-bg-text">
                ログイン
              </h2>
              <p className="mt-2 text-sm [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">
                {siteData?.name}へのログイン
              </p>
            </div>

            <ThemedCard className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-100 border-2 border-red-500 rounded-theme p-4">
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-red-700">{error}</p>
                  </div>
                )}

                <div>
                  <Label htmlFor="email" className="block text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-1">
                    メールアドレス
                  </Label>
                  <ThemedInput
                    id="email"
                    type="email"
                    placeholder="test@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="block text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-1">
                    パスワード
                  </Label>
                  <ThemedInput
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="********"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <a
                    href="/reset-password"
                    className="text-sm [font-weight:var(--theme-font-weight-button)] text-black hover:text-theme-primary underline underline-offset-2"
                  >
                    パスワードを忘れた場合
                  </a>
                </div>

                {RECAPTCHA_SITE_KEY && (
                  <div className="flex justify-end">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                    />
                  </div>
                )}

                <ThemedButton
                  type="submit"
                  variant="primary"
                  className="w-full py-3"
                >
                  ログインする
                </ThemedButton>
              </form>

              <div className="mt-6 text-center">
                <span className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 mr-1">アカウントをお持ちでないですか？</span>
                <a
                  href="/signup"
                  className="text-sm [font-weight:var(--theme-font-weight)] text-black hover:text-theme-primary underline underline-offset-2"
                >
                  新規登録
                </a>
              </div>
            </ThemedCard>
          </div>
        </section>
      </div>
    </>
  );
};

export default LoginPage;
